import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import adminSurveyService from "../../services/adminSurveyService";

export default function SurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);


  useEffect(() => {
    fetchAdminSurvey();
  }, []);

  const fetchAdminSurvey = async () => {
    try {
      setLoading(true);
      const surveysData = await adminSurveyService.getAllAdminSurveys();
      setSurveys(Array.isArray(surveysData) ? surveysData : []);
    } catch (error) {
      console.error('Error fetching admin survey:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, active) => {
    try {
      await adminSurveyService.toggleAdminSurveyStatus(id, !active);
      // Update local state. If activating, deactivate all others.
      setSurveys(prev => {
        const activating = !active;
        if (activating) {
          return prev.map(s => s.id === id ? { ...s, active: true } : { ...s, active: false });
        }
        return prev.map(s => s.id === id ? { ...s, active: false } : s);
      });
    } catch (error) {
      console.error('Error toggling admin survey status:', error);
    }
  };

  const handleDeleteClick = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteModal(true);
  };

  const confirmDeleteSurvey = async () => {
    if (!surveyToDelete) return;

    setIsDeleting(true);
    try {
      await adminSurveyService.deleteAdminSurvey(surveyToDelete.id);
      setSurveys(prev => prev.filter(s => s.id !== surveyToDelete.id));
      setShowDeleteModal(false);
      setSurveyToDelete(null);
    } catch (error) {
      console.error('Error deleting admin survey:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (survey) => {
    // Navigate to edit page with survey type
    window.location.href = `/dashboard/surveys/edit/${survey.type}/${survey.id}`;
  };

  const handleAnalytics = (survey) => {
    // Navigate to analytics page
    window.location.href = `/dashboard/surveys/analytics/${survey.id}`;
  };



  const activeSurveyId = surveys.find(s => s.active)?.id || null;
  const hasActive = Boolean(activeSurveyId);

  if (loading) {
    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-1">Admin Survey</h1>
            <p className="text-gray-700">Manage your admin feedback survey</p>
          </div>
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                <div className="space-y-2">
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Admin Survey</h1>
          <p className="text-gray-700">Manage your admin feedback survey</p>
        </div>
        {!hasActive && (
          <Link
            to="/dashboard/surveys/create"
            className="bg-[#2962FF] hover:bg-[#2962FF]/90 text-white px-4 py-2 rounded w-full sm:w-auto flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create New Survey
          </Link>
        )}
      </div>

      {surveys.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="text-4xl font-bold text-[#2962FF] mb-4">📊</div>
          <h2 className="text-xl font-semibold mb-2">No Admin Survey Found</h2>
          <p className="text-gray-600">
            You haven't created an admin survey yet. Create one to collect user feedback.
          </p>
        </div>
      ) : (
        <>
          {hasActive && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-blue-800 text-sm">
                    <strong>Note:</strong> You can only have one active admin survey at a time. To create a new survey, please deactivate your existing survey first.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {surveys.length > 0 && (
        <div className="space-y-4">
          {surveys.map((survey) => {
            const toggleDisabled = hasActive && activeSurveyId !== survey.id && !survey.active;
            return (
              <div
                key={survey.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-4 md:p-6">
                  {/* Desktop layout */}
                  <div className="hidden md:flex md:justify-between md:items-center">
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium text-gray-900">{survey.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{survey.type}</span>
                        <span>•</span>
                        <span>Last updated {moment(survey.updatedAt).fromNow()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <label className={`inline-flex items-center ${toggleDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!survey.active}
                            disabled={toggleDisabled}
                            onChange={() => handleToggleStatus(survey.id, survey.active)}
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full
                             peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                             peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                             after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5
                             after:transition-all after:duration-700 peer-checked:bg-[#2962FF]">
                          </div>
                        </label>
                        <span className="text-sm text-gray-600 transition-all duration-700">
                          {survey.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <button
                        className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm h-9 flex items-center"
                        onClick={() => handleAnalytics(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="9" y1="21" x2="9" y2="9" />
                        </svg>
                        Analytics
                      </button>

                      <button
                        className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm h-9 flex items-center"
                        onClick={() => handleEdit(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        Edit
                      </button>

                      <button
                        className="border border-red-300 text-red-700 px-3 py-1.5 rounded text-sm h-9 flex items-center hover:bg-red-50"
                        onClick={() => handleDeleteClick(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Mobile layout */}
                  <div className="md:hidden space-y-3">
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium text-gray-900">{survey.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{survey.type}</span>
                        <span>•</span>
                        <span>Last updated {moment(survey.updatedAt).fromNow()}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2">
                        <label className={`inline-flex items-center ${toggleDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!survey.active}
                            disabled={toggleDisabled}
                            onChange={() => handleToggleStatus(survey.id, survey.active)}
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full
                             peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
                             peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                             after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5
                             after:transition-all after:duration-700 peer-checked:bg-[#2962FF]">
                          </div>
                        </label>
                        <span className="text-sm text-gray-600 transition-all duration-700">
                          {survey.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm flex items-center"
                        onClick={() => handleAnalytics(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="9" y1="21" x2="9" y2="9" />
                        </svg>
                        Analytics
                      </button>

                      <button
                        className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm flex items-center"
                        onClick={() => handleEdit(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                        Edit
                      </button>

                      <button
                        className="border border-red-300 text-red-700 px-3 py-1.5 rounded text-sm flex items-center hover:bg-red-50"
                        onClick={() => handleDeleteClick(survey)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">Delete Survey</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete "{surveyToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                onClick={confirmDeleteSurvey}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
